/**
 * ActorSearchSelect - Reusable actor picker for team member assignment (DRR-061)
 *
 * Loads actors via GraphQL, allows searching by name.
 * Used for: team lead, deputy lead, stakeholder assignment.
 */

import { Select } from '@mantine/core';
import { useMemo } from 'react';
import { useListActorsForSelectQuery } from '@/generated/graphql';

interface ActorSearchSelectProps {
  label: string;
  value?: string | null;
  onChange: (actorId: string | null) => void;
  excludeIds?: string[];
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
}

export function ActorSearchSelect({
  label,
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Search actors...',
  clearable = true,
  disabled = false,
}: ActorSearchSelectProps) {
  const { data, loading } = useListActorsForSelectQuery();

  const actorOptions = useMemo(() => {
    const actors = data?.listActors ?? [];
    return actors
      .filter((a: any) => a.active && !excludeIds.includes(a.id))
      .map((a: any) => ({
        value: a.id,
        label: `${a.name} (${a.actorType})`,
      }));
  }, [data, excludeIds]);

  return (
    <Select
      label={label}
      placeholder={placeholder}
      data={actorOptions}
      value={value ?? null}
      onChange={onChange}
      searchable
      clearable={clearable}
      disabled={disabled || loading}
      nothingFoundMessage={loading ? 'Loading...' : 'No actors found'}
    />
  );
}
